import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell } from 'recharts';
const EnergyConsumptionDashboard: React.FC = () => {
  // KPI Data
  const kpis = [{
    title: 'Energy Cost',
    value: '₹98.5 Cr',
    change: '+3.2%',
    isPositive: false,
    subtitle: 'FY24 total'
  }, {
    title: 'kWh per Unit',
    value: '0.42 kWh',
    change: '-2.1%',
    isPositive: true,
    subtitle: 'Current efficiency'
  }, {
    title: 'Peak Consumption',
    value: '18.5%',
    change: '+1.2%',
    isPositive: false,
    subtitle: 'Of total capacity'
  }];
  // Energy Trend Data (Last 12 Months)
  const energyTrendData = [{
    month: 'Jan',
    consumption: 1050,
    cost: 8.9,
    kwhPerUnit: 0.45
  }, {
    month: 'Feb',
    consumption: 1020,
    cost: 8.6,
    kwhPerUnit: 0.44
  }, {
    month: 'Mar',
    consumption: 1080,
    cost: 9.2,
    kwhPerUnit: 0.44
  }, {
    month: 'Apr',
    consumption: 1040,
    cost: 8.8,
    kwhPerUnit: 0.43
  }, {
    month: 'May',
    consumption: 1060,
    cost: 9.0,
    kwhPerUnit: 0.43
  }, {
    month: 'Jun',
    consumption: 1030,
    cost: 8.7,
    kwhPerUnit: 0.42
  }, {
    month: 'Jul',
    consumption: 1070,
    cost: 9.1,
    kwhPerUnit: 0.43
  }, {
    month: 'Aug',
    consumption: 1045,
    cost: 8.9,
    kwhPerUnit: 0.42
  }, {
    month: 'Sep',
    consumption: 1025,
    cost: 8.7,
    kwhPerUnit: 0.42
  }, {
    month: 'Oct',
    consumption: 1055,
    cost: 8.9,
    kwhPerUnit: 0.41
  }, {
    month: 'Nov',
    consumption: 1035,
    cost: 8.8,
    kwhPerUnit: 0.41
  }, {
    month: 'Dec',
    consumption: 1020,
    cost: 8.6,
    kwhPerUnit: 0.4
  }];
  // Plant-wise Energy Consumption Data
  const plantEnergyData = [{
    plant: 'Manesar',
    consumption: 2950,
    cost: 24.5,
    kwhPerUnit: 0.43,
    peakLoad: 485,
    color: '#3b82f6'
  }, {
    plant: 'Chennai',
    consumption: 3150,
    cost: 26.8,
    kwhPerUnit: 0.44,
    peakLoad: 520,
    color: '#10b981'
  }, {
    plant: 'Pune',
    consumption: 2350,
    cost: 19.5,
    kwhPerUnit: 0.43,
    peakLoad: 390,
    color: '#f59e0b'
  }, {
    plant: 'Bangalore',
    consumption: 2180,
    cost: 18.2,
    kwhPerUnit: 0.44,
    peakLoad: 365,
    color: '#ef4444'
  }];
  // Consumption vs Output Scatter Data
  const consumptionOutputScatterData = [{
    plant: 'Manesar',
    output: 685000,
    consumption: 2950,
    efficiency: 92,
    size: 300
  }, {
    plant: 'Chennai',
    output: 725000,
    consumption: 3150,
    efficiency: 94,
    size: 320
  }, {
    plant: 'Pune',
    output: 545000,
    consumption: 2350,
    efficiency: 90,
    size: 280
  }, {
    plant: 'Bangalore',
    output: 495000,
    consumption: 2180,
    efficiency: 93,
    size: 290
  }];
  // Energy Usage Table Data
  const energyUsageData = [{
    plant: 'Manesar',
    month: 'December',
    totalConsumption: 245,
    peakConsumption: 485,
    avgConsumption: 340,
    cost: 2.05,
    costPerKwh: 8.37,
    kwhPerUnit: 0.43,
    output: 57000,
    peakLoadPercent: 18.2,
    efficiencyScore: 92,
    carbonFootprint: 122.5
  }, {
    plant: 'Chennai',
    month: 'December',
    totalConsumption: 262,
    peakConsumption: 520,
    avgConsumption: 365,
    cost: 2.23,
    costPerKwh: 8.51,
    kwhPerUnit: 0.44,
    output: 60400,
    peakLoadPercent: 19.5,
    efficiencyScore: 94,
    carbonFootprint: 131.0
  }, {
    plant: 'Pune',
    month: 'December',
    totalConsumption: 196,
    peakConsumption: 390,
    avgConsumption: 272,
    cost: 1.63,
    costPerKwh: 8.32,
    kwhPerUnit: 0.43,
    output: 45400,
    peakLoadPercent: 16.8,
    efficiencyScore: 90,
    carbonFootprint: 98.0
  }, {
    plant: 'Bangalore',
    month: 'December',
    totalConsumption: 182,
    peakConsumption: 365,
    avgConsumption: 253,
    cost: 1.52,
    costPerKwh: 8.35,
    kwhPerUnit: 0.44,
    output: 41200,
    peakLoadPercent: 17.5,
    efficiencyScore: 93,
    carbonFootprint: 91.0
  }, {
    plant: 'Manesar',
    month: 'November',
    totalConsumption: 250,
    peakConsumption: 490,
    avgConsumption: 345,
    cost: 2.09,
    costPerKwh: 8.36,
    kwhPerUnit: 0.44,
    output: 56800,
    peakLoadPercent: 18.5,
    efficiencyScore: 91,
    carbonFootprint: 125.0
  }, {
    plant: 'Chennai',
    month: 'November',
    totalConsumption: 268,
    peakConsumption: 525,
    avgConsumption: 370,
    cost: 2.28,
    costPerKwh: 8.51,
    kwhPerUnit: 0.45,
    output: 59600,
    peakLoadPercent: 19.8,
    efficiencyScore: 93,
    carbonFootprint: 134.0
  }, {
    plant: 'Pune',
    month: 'November',
    totalConsumption: 201,
    peakConsumption: 395,
    avgConsumption: 278,
    cost: 1.67,
    costPerKwh: 8.31,
    kwhPerUnit: 0.44,
    output: 45200,
    peakLoadPercent: 17.2,
    efficiencyScore: 89,
    carbonFootprint: 100.5
  }, {
    plant: 'Bangalore',
    month: 'November',
    totalConsumption: 186,
    peakConsumption: 370,
    avgConsumption: 258,
    cost: 1.55,
    costPerKwh: 8.33,
    kwhPerUnit: 0.45,
    output: 40800,
    peakLoadPercent: 17.8,
    efficiencyScore: 92,
    carbonFootprint: 93.0
  }, {
    plant: 'Manesar',
    month: 'October',
    totalConsumption: 255,
    peakConsumption: 495,
    avgConsumption: 352,
    cost: 2.13,
    costPerKwh: 8.35,
    kwhPerUnit: 0.43,
    output: 58500,
    peakLoadPercent: 18.8,
    efficiencyScore: 92,
    carbonFootprint: 127.5
  }, {
    plant: 'Chennai',
    month: 'October',
    totalConsumption: 272,
    peakConsumption: 530,
    avgConsumption: 375,
    cost: 2.31,
    costPerKwh: 8.49,
    kwhPerUnit: 0.44,
    output: 61200,
    peakLoadPercent: 20.0,
    efficiencyScore: 94,
    carbonFootprint: 136.0
  }, {
    plant: 'Pune',
    month: 'October',
    totalConsumption: 205,
    peakConsumption: 400,
    avgConsumption: 283,
    cost: 1.71,
    costPerKwh: 8.34,
    kwhPerUnit: 0.43,
    output: 46800,
    peakLoadPercent: 17.5,
    efficiencyScore: 90,
    carbonFootprint: 102.5
  }, {
    plant: 'Bangalore',
    month: 'October',
    totalConsumption: 190,
    peakConsumption: 375,
    avgConsumption: 263,
    cost: 1.58,
    costPerKwh: 8.32,
    kwhPerUnit: 0.44,
    output: 42500,
    peakLoadPercent: 18.0,
    efficiencyScore: 93,
    carbonFootprint: 95.0
  }];
  return <div data-id="element-5214">
      <DescriptionPanel title="Energy Consumption & Cost" description="Comprehensive energy consumption tracking and cost analysis across all manufacturing plants. Monitor energy efficiency, identify optimization opportunities, and track sustainability metrics to reduce costs and carbon footprint." insights={['Energy cost at ₹98.5 Cr - 3.2% increase YoY driven by higher tariffs', 'kWh per unit improved to 0.42 - 2.1% efficiency gain through optimization', 'Chennai plant shows highest consumption but best efficiency score (94)', 'Peak load at 18.5% of capacity - opportunity to shift production for cost savings']} data-id="element-5215" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-5216">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5217">
          Energy Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-5218">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-5219">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-5220">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-5221">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-5222">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-5223">
                {kpi.isPositive ? <TrendingDownIcon size={16} className="mr-1" data-id="element-5224" /> : <TrendingUpIcon size={16} className="mr-1" data-id="element-5225" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Energy Trend */}
      <Card className="p-6 mb-8" data-id="element-5226">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5227">
          Energy Consumption Trend (Last 12 Months)
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-5228">
          Monthly consumption (MWh), cost (₹ Cr), and efficiency (kWh/unit)
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-5229">
          <ComposedChart data={energyTrendData} data-id="element-5230">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5231" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-5232" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} label={{
            value: 'Consumption (MWh)',
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: 12
            }
          }} data-id="element-5233" />
            <YAxis yAxisId="right" orientation="right" tick={{
            fontSize: 12
          }} label={{
            value: 'kWh per Unit',
            angle: 90,
            position: 'insideRight',
            style: {
              fontSize: 12
            }
          }} domain={[0.35, 0.5]} data-id="element-5234" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-5235" />
            <Legend data-id="element-5236" />
            <Bar yAxisId="left" dataKey="consumption" fill="#3b82f6" name="Consumption (MWh)" data-id="element-5237" />
            <Line yAxisId="right" type="monotone" dataKey="kwhPerUnit" stroke="#10b981" strokeWidth={3} name="kWh per Unit" dot={{
            r: 4
          }} data-id="element-5238" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5239">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-5240">
            <div className="text-center" data-id="element-5241">
              <div className="text-gray-600" data-id="element-5242">Current Month</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-5243">1,020 MWh</div>
              <div className="text-xs text-gray-500" data-id="element-5244">December</div>
            </div>
            <div className="text-center" data-id="element-5245">
              <div className="text-gray-600" data-id="element-5246">YTD Total</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5247">12,530 MWh</div>
              <div className="text-xs text-gray-500" data-id="element-5248">FY24 total</div>
            </div>
            <div className="text-center" data-id="element-5249">
              <div className="text-gray-600" data-id="element-5250">Avg Monthly</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5251">1,044 MWh</div>
              <div className="text-xs text-gray-500" data-id="element-5252">12-month average</div>
            </div>
            <div className="text-center" data-id="element-5253">
              <div className="text-gray-600" data-id="element-5254">Efficiency Gain</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5255">-11%</div>
              <div className="text-xs text-gray-500" data-id="element-5256">vs Jan kWh/unit</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Plant-wise Energy Consumption and Consumption vs Output Scatter */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-5257">
        {/* Plant-wise Energy Consumption */}
        <Card className="p-6" data-id="element-5258">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5259">
            Plant-wise Energy Consumption
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-5260">
            Total consumption (MWh) and cost (₹ Cr)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-5261">
            <ComposedChart data={plantEnergyData} layout="vertical" data-id="element-5262">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5263" />
              <XAxis type="number" tick={{
              fontSize: 11
            }} data-id="element-5264" />
              <YAxis dataKey="plant" type="category" width={80} tick={{
              fontSize: 11
            }} data-id="element-5265" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-5266" />
              <Legend data-id="element-5267" />
              <Bar dataKey="consumption" fill="#3b82f6" name="Consumption (MWh)" data-id="element-5268">
                {plantEnergyData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-5269" />)}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5270">
            <div className="space-y-2" data-id="element-5271">
              {plantEnergyData.map((item, index) => <div key={index} className="flex items-center justify-between text-sm" data-id="element-5272">
                  <div className="flex items-center" data-id="element-5273">
                    <div className="w-3 h-3 rounded mr-2" style={{
                  backgroundColor: item.color
                }} data-id="element-5274" />
                    <span className="text-gray-700" data-id="element-5275">{item.plant}</span>
                  </div>
                  <div className="text-right" data-id="element-5276">
                    <span className="font-semibold text-gray-900" data-id="element-5277">
                      {item.consumption} MWh
                    </span>
                    <span className="text-xs text-gray-600 ml-2" data-id="element-5278">
                      (₹{item.cost} Cr)
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </Card>
        {/* Consumption vs Output Scatter */}
        <Card className="p-6" data-id="element-5279">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5280">
            Energy Consumption vs Output Analysis
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-5281">
            Relationship between energy consumption and production output
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-5282">
            <ScatterChart data-id="element-5283">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5284" />
              <XAxis type="number" dataKey="output" name="Output" tick={{
              fontSize: 11
            }} label={{
              value: 'Production Output (Units)',
              position: 'insideBottom',
              offset: -5,
              style: {
                fontSize: 11
              }
            }} data-id="element-5285" />
              <YAxis type="number" dataKey="consumption" name="Consumption" unit=" MWh" tick={{
              fontSize: 11
            }} label={{
              value: 'Energy Consumption (MWh)',
              angle: -90,
              position: 'insideLeft',
              style: {
                fontSize: 11
              }
            }} data-id="element-5286" />
              <ZAxis type="number" dataKey="size" range={[200, 400]} data-id="element-5287" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} cursor={{
              strokeDasharray: '3 3'
            }} content={({
              active,
              payload
            }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-sm" data-id="element-5289">
                        <p className="font-semibold text-gray-800 mb-1" data-id="element-5290">
                          {data.plant}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-5291">
                          Output: {data.output.toLocaleString()} units
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-5292">
                          Consumption: {data.consumption} MWh
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-5293">
                          Efficiency: {data.efficiency}%
                        </p>
                      </div>;
              }
              return null;
            }} data-id="element-5288" />
              <Scatter name="Plants" data={consumptionOutputScatterData} fill="#3b82f6" data-id="element-5294" />
            </ScatterChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5295">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-5296">
              <div className="text-center" data-id="element-5297">
                <div className="text-gray-600" data-id="element-5298">Most Efficient</div>
                <div className="text-lg font-bold text-green-600" data-id="element-5299">Chennai</div>
                <div className="text-xs text-gray-500" data-id="element-5300">94% efficiency</div>
              </div>
              <div className="text-center" data-id="element-5301">
                <div className="text-gray-600" data-id="element-5302">Best kWh/Unit</div>
                <div className="text-lg font-bold text-green-600" data-id="element-5303">Manesar</div>
                <div className="text-xs text-gray-500" data-id="element-5304">0.43 kWh/unit</div>
              </div>
              <div className="text-center" data-id="element-5305">
                <div className="text-gray-600" data-id="element-5306">Highest Output</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-5307">Chennai</div>
                <div className="text-xs text-gray-500" data-id="element-5308">725K units</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Energy Usage Table */}
      <Card className="p-6" data-id="element-5309">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5310">
          Detailed Energy Usage Report
        </h3>
        <div className="overflow-x-auto" data-id="element-5311">
          <table className="w-full" data-id="element-5312">
            <thead data-id="element-5313">
              <tr className="border-b-2 border-gray-300" data-id="element-5314">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5315">
                  Plant
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5316">
                  Month
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5317">
                  Total (MWh)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5318">
                  Peak (kW)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5319">
                  Avg (kW)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5320">
                  Cost (₹ Cr)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5321">
                  Cost/kWh (₹)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5322">
                  kWh/Unit
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5323">
                  Output
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5324">
                  Peak Load %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5325">
                  Efficiency
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5326">
                  Carbon (tCO₂)
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5327">
              {energyUsageData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-5328">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-5329">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-5330">
                    {row.month}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5331">
                    {row.totalConsumption}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5332">
                    {row.peakConsumption}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5333">
                    {row.avgConsumption}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-blue-600" data-id="element-5334">
                    ₹{row.cost}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5335">
                    ₹{row.costPerKwh}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-900" data-id="element-5336">
                    {row.kwhPerUnit}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5337">
                    {row.output.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5338">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.peakLoadPercent <= 17.5 ? 'bg-green-100 text-green-700' : row.peakLoadPercent <= 19.0 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5339">
                      {row.peakLoadPercent}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5340">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.efficiencyScore >= 93 ? 'bg-green-100 text-green-700' : row.efficiencyScore >= 91 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5341">
                      {row.efficiencyScore}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5342">
                    {row.carbonFootprint}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-5343">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-5344">
            <div className="text-center" data-id="element-5345">
              <div className="text-gray-600" data-id="element-5346">Total Consumption</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5347">2,868 MWh</div>
              <div className="text-xs text-gray-500" data-id="element-5348">last 3 months</div>
            </div>
            <div className="text-center" data-id="element-5349">
              <div className="text-gray-600" data-id="element-5350">Total Cost</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-5351">₹24.0 Cr</div>
              <div className="text-xs text-gray-500" data-id="element-5352">last 3 months</div>
            </div>
            <div className="text-center" data-id="element-5353">
              <div className="text-gray-600" data-id="element-5354">Avg kWh/Unit</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5355">0.43</div>
              <div className="text-xs text-gray-500" data-id="element-5356">efficiency</div>
            </div>
            <div className="text-center" data-id="element-5357">
              <div className="text-gray-600" data-id="element-5358">Peak Load Avg</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-5359">18.1%</div>
              <div className="text-xs text-gray-500" data-id="element-5360">of capacity</div>
            </div>
            <div className="text-center" data-id="element-5361">
              <div className="text-gray-600" data-id="element-5362">Total Output</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5363">614.3K</div>
              <div className="text-xs text-gray-500" data-id="element-5364">units</div>
            </div>
            <div className="text-center" data-id="element-5365">
              <div className="text-gray-600" data-id="element-5366">Carbon Footprint</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5367">1,434 tCO₂</div>
              <div className="text-xs text-gray-500" data-id="element-5368">last 3 months</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default EnergyConsumptionDashboard;